/**
 * Onboarding · Step 3 — Drag the AeroCarry brief onto the canvas.
 *
 * Renders a draggable pill for the AeroCarry Product Brief PDF, right-
 * anchored so the open canvas lies to the left. On drop, the file_record
 * node appears at the cursor; this step samples the node's resulting
 * canvas position from the canvas nodes mirror and
 * persists it as `data.pdfDropPoint` in Loro so Steps 6 and 8 can
 * anchor their image-column and deck layouts off it.
 *
 * Self-seeding: the dialog no longer pre-uploads the PDFs on canvas
 * creation. Instead, this step fetches and uploads the AeroCarry brief
 * itself on mount (fire-and-forget) so the file_record is available as
 * soon as the step appears. While the upload is in flight the step shows
 * a placeholder pill; once the record appears in `useCanvasFileRecords`
 * the real draggable pill renders.
 *
 * Detection: advances once the target file record id appears as a node
 * in the canvas nodes mirror. At that point, reads its `.position`
 * and persists to Loro before advancing.
 */

import { useCanvasFileRecords } from "@/api/fileRecords";
import type { DraggableData } from "@/dnd/ids";
import { buildCopyPayloadFromRecord } from "@/dnd/payloads";
import { performFileRecordUpload } from "@/hooks/useFileRecordUpload";
import type { FileRecord } from "@cohesive/api-client";
import { useDraggable } from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { memo, useEffect, useMemo, useRef, type CSSProperties } from "react";
import { useCanvasApi } from "../../../../context/CanvasContext";
import { useCanvasNodesMirror } from "../../../../context/CanvasNodesMirrorContext";
import { useCanvasOnboardingUpdate } from "../../../../context/CanvasOnboardingContext";
import { useCanvasRoute } from "../../../../context/CanvasRouteContext";
import {
  BRIEF_DROP_TARGET_HEIGHT,
  BRIEF_DROP_TARGET_WIDTH,
} from "../shared/constants";
import { Flourish, StepHeader } from "../shared/StepHeader";
import type { StepProps } from "../shared/types";

/** Brief pause after drop before exiting so the user can see the node land. */
const POST_DROP_READ_MS = 700;

/** CDN URL for the AeroCarry Product Brief PDF. */
const BRIEF_PDF_URL =
  "https://file.dev.cohesive.ai/onboarding/AeroCarry___Product_Brief.pdf";
const BRIEF_PDF_NAME = "AeroCarry - Product Brief.pdf";

/** Name fragments that identify the AeroCarry Product Brief. */
const BRIEF_NAME_HINTS = [
  "aerocarry",
  "product brief",
  "product_brief",
  "aerocarry___product_brief",
];

function isAeroCarryBrief(record: FileRecord): boolean {
  const lower = record.path.toLowerCase();
  return BRIEF_NAME_HINTS.some((hint) => lower.includes(hint));
}

function isFolder(record: FileRecord): boolean {
  return record.path.endsWith("/");
}

export const DragBriefStep = memo(function DragBriefStep({
  id,
  onAdvance,
}: StepProps) {
  const update = useCanvasOnboardingUpdate();
  const { canvasId } = useCanvasRoute();
  const canvas = useCanvasApi();
  const queryClient = useQueryClient();

  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;

  const { data: fileRecords = [] } = useCanvasFileRecords(
    canvasId || undefined,
  );

  // Self-seed the AeroCarry brief on mount (fire-and-forget). The dialog
  // no longer pre-uploads PDFs, so the brief is seeded here instead. The
  // upload runs once; React Query cache invalidation makes the file appear
  // in `useCanvasFileRecords` automatically, rendering the draggable pill.
  const seedStartedRef = useRef(false);
  useEffect(() => {
    if (!canvasId) return;
    if (seedStartedRef.current) return;
    seedStartedRef.current = true;
    void (async () => {
      try {
        const res = await fetch(BRIEF_PDF_URL);
        if (!res.ok) return;
        const blob = await res.blob();
        const file = new File([blob], BRIEF_PDF_NAME, {
          type: "application/pdf",
        });
        await performFileRecordUpload({
          file,
          target: { class: "canvas", canvas_id: canvasId },
          queryClient,
        });
      } catch {
        // Swallow — the step still renders the placeholder and waits.
      }
    })();
  }, [canvasId, queryClient]);
  const rawNodes = useCanvasNodesMirror(canvasId);

  // Seal the target file record once resolved. We prefer the AeroCarry
  // brief; fall back to the first unplaced non-folder file.
  const placedIds = useMemo(
    () => new Set(rawNodes.map((n) => n.id)),
    [rawNodes],
  );

  const targetRef = useRef<FileRecord | null>(null);
  if (targetRef.current === null && fileRecords.length > 0) {
    // Prefer the unplaced AeroCarry brief.
    const brief = fileRecords.find(
      (r) => !isFolder(r) && !placedIds.has(r.id) && isAeroCarryBrief(r),
    );
    // Fall back to the first unplaced non-folder file.
    const fallback = fileRecords.find(
      (r) => !isFolder(r) && !placedIds.has(r.id),
    );
    targetRef.current = brief ?? fallback ?? null;
  }

  const target = targetRef.current;
  const targetPlaced = target ? placedIds.has(target.id) : false;

  // If all records are placed already (worked-in canvas), advance immediately.
  const sealedEmpty = fileRecords.length > 0 && targetRef.current === null;

  // Viewport stays unlocked — the canvas-board droppable needs to receive
  // the dnd-kit drag. No spotlight — the pill in the card is the affordance.
  useEffect(() => {
    update({ lockedViewport: false, highlightTargetId: null });
    return () => {
      update({ lockedViewport: false, highlightTargetId: null });
    };
  }, [update]);

  // Drop-zone bounds in canvas coordinates. The widget that hosts the
  // dashed outline is seeded at canvas origin (see
  // useSeedOnboardingWidget), and the outline is centered on it — so
  // the accept rectangle is symmetric around (0, 0). A drop is valid
  // if the file_record node's top-left position (the cursor's release
  // point in canvas space) falls inside this rectangle.
  const DROP_HALF_W = BRIEF_DROP_TARGET_WIDTH / 2;
  const DROP_HALF_H = BRIEF_DROP_TARGET_HEIGHT / 2;
  const isInsideDropZone = (pos: { x: number; y: number }) =>
    pos.x >= -DROP_HALF_W &&
    pos.x <= DROP_HALF_W &&
    pos.y >= -DROP_HALF_H &&
    pos.y <= DROP_HALF_H;

  // Watch for drops. If the user releases inside the dashed zone,
  // persist + advance. If they release outside, immediately remove
  // the node from the canvas so the draggable pill re-renders and
  // they can try again — no advance, no Loro state churn. Gated on
  // the targetPlaced edge only, so the rejection branch fires exactly
  // once per drop attempt (placedIds → targetPlaced → false again
  // after removeNode lets the next attempt run cleanly).
  const advancedRef = useRef(false);
  useEffect(() => {
    if (sealedEmpty) {
      if (advancedRef.current) return;
      advancedRef.current = true;
      const t = window.setTimeout(() => onAdvanceRef.current(), 0);
      return () => window.clearTimeout(t);
    }

    if (!targetPlaced || !target) return;
    const node = rawNodes.find((n) => n.id === target.id);
    if (!node) return;

    if (!isInsideDropZone(node.position)) {
      // Out-of-zone drop: reject by removing the placed node. The
      // file_record itself persists so the draggable pill keeps the
      // same identity on retry.
      canvas.removeNode(node.id);
      return;
    }

    if (advancedRef.current) return;
    advancedRef.current = true;

    canvas.updateNodeData(id, {
      pdfNodeId: target.id,
      pdfDropPoint: node.position,
    });

    const t = window.setTimeout(
      () => onAdvanceRef.current(),
      POST_DROP_READ_MS,
    );
    return () => window.clearTimeout(t);
    // rawNodes is intentionally referenced (not a dep) so we read the
    // freshest position on the placement-edge transition without
    // re-running between drops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPlaced, sealedEmpty]);

  return (
    <>
      <div style={{} as CSSProperties}>
        <StepHeader
          heading={
            <>
              Anything in your workspace becomes a <Flourish>node</Flourish>.
            </>
          }
          lede={
            <>
              Drag this file out of the card and onto the canvas — files,
              integrations, AI outputs, whatever you have, they all live as
              nodes you can move, group, and chat about.
            </>
          }
        />
      </div>

      <div
        className="_co_onb_settle_3"
        style={{
          marginTop: "var(--co-onb-stack-md)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "var(--co-onb-stack-sm)",
          width: "100%",
        }}
      >
        <div
          style={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          {/* Click-and-drag demo cursor — loops a virtual cursor onto
              the pill, presses, drags right toward the canvas, resets.
              Hidden once the user has dropped the file. */}
          {!targetPlaced && target && (
            <>
              <span aria-hidden className="_co_onb_drag_click_ring" />
              <span aria-hidden className="_co_onb_drag_cursor">
                <DragCursorGlyph />
              </span>
            </>
          )}

          <div style={{ position: "relative", zIndex: 2 }}>
            {target ? (
              <DraggableFilePill record={target} canvasId={canvasId ?? ""} />
            ) : (
              <PlaceholderPill />
            )}
          </div>
        </div>

        {/* Step indicator */}
        <div
          aria-hidden
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginTop: 4,
            padding: "8px 14px",
            borderRadius: 999,
            background: targetPlaced
              ? "var(--co-rust-soft, rgba(176, 87, 49, 0.12))"
              : "var(--co-snap-fill)",
            border: `1px solid ${
              targetPlaced ? "var(--co-rust)" : "var(--co-track-strong)"
            }`,
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "-0.005em",
            color: targetPlaced ? "var(--co-rust)" : "var(--co-fg)",
            transition:
              "color 320ms ease, background-color 320ms ease, border-color 320ms ease",
          }}
        >
          <span>
            {targetPlaced
              ? "On the canvas"
              : target
                ? "\u2190 Drag onto the canvas"
                : "Preparing your starter files…"}
          </span>
        </div>
      </div>
    </>
  );
});

// ─── Subcomponents ───────────────────────────────────────────────────────────

const DraggableFilePill = memo(function DraggableFilePill({
  record,
  canvasId,
}: {
  record: FileRecord;
  canvasId: string;
}) {
  const payload = useMemo(
    () =>
      buildCopyPayloadFromRecord(
        record,
        { canvasId },
        { placed: false, parentSectionId: null },
      ),
    [record, canvasId],
  );

  const draggableData: DraggableData = useMemo(
    () => ({
      kind: "files",
      payloads: [payload],
      primaryName: payload.name,
    }),
    [payload],
  );

  const draggable = useDraggable({
    id: `onboarding-drag-brief:${record.id}`,
    data: draggableData,
  });

  return (
    <button
      type="button"
      ref={draggable.setNodeRef}
      onPointerDown={(e) => e.stopPropagation()}
      className="nodrag _co_onb_drag_pill"
      aria-label={`Drag ${payload.name} onto the canvas`}
      {...draggable.attributes}
      {...draggable.listeners}
      style={{
        appearance: "none",
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        width: 148,
        height: 148,
        padding: "18px 14px",
        background: "var(--co-snap-fill)",
        border: "1.5px dashed var(--co-track-strong)",
        borderRadius: 18,
        color: "var(--co-fg)",
        fontFamily: "inherit",
        fontSize: 12,
        fontWeight: 500,
        letterSpacing: "-0.005em",
        cursor: draggable.isDragging ? "grabbing" : "grab",
        opacity: draggable.isDragging ? 0.4 : 1,
        textAlign: "center",
        boxShadow: "var(--co-cta-shadow)",
        transition:
          "background-color 200ms cubic-bezier(0.2,0,0,1), border-color 200ms cubic-bezier(0.2,0,0,1), opacity 160ms ease",
        touchAction: "none",
      }}
    >
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          width: 48,
          height: 48,
          borderRadius: 12,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--co-paper)",
          border: "1px solid var(--co-border)",
          color: "var(--co-rust)",
        }}
      >
        <FileText size={28} strokeWidth={1.6} />
      </span>
      <span
        style={{
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {payload.name}
      </span>
    </button>
  );
});

/** Classic arrow cursor glyph in SVG — drawn in currentColor with a
 *  contrasting outline so it reads on both light and dark canvases. */
const DragCursorGlyph = memo(function DragCursorGlyph() {
  return (
    <svg
      width="42"
      height="42"
      viewBox="0 0 28 28"
      fill="none"
      style={{ display: "block" }}
    >
      <path
        d="M5 3 L5 22 L10.5 17.5 L13.5 24.5 L16.5 23.2 L13.5 16.2 L21 16.2 Z"
        fill="currentColor"
        stroke="var(--co-cursor-stroke, #ffffff)"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
});

const PlaceholderPill = memo(function PlaceholderPill() {
  return (
    <div
      aria-hidden
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        width: 148,
        height: 148,
        background: "transparent",
        border: "1.5px dashed var(--co-track)",
        borderRadius: 18,
        color: "var(--co-fg-muted)",
        fontSize: 12,
        fontWeight: 500,
        opacity: 0.7,
        textAlign: "center",
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 48,
          height: 48,
          borderRadius: 12,
          background: "var(--co-snap-fill)",
        }}
      />
      <span style={{ fontVariantNumeric: "tabular-nums" }}>Loading file…</span>
    </div>
  );
});
